import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { FieldValue } from 'api/tasks';
import { FormGroup } from 'components/atoms/form-group';
import { StatusPicker } from 'components/atoms/status-picker';
import { useStatusField } from 'data/fields/hooks';
import { Task } from 'data/tasks';
import { updateTaskSagaAction } from 'data/tasks/actions';
import { StyledLabelForGhostInput } from '../header/styles';

interface TaskStatusPickerProps {
  data: Task;
  disabled?: boolean;
}

export const TaskStatusPicker: FC<TaskStatusPickerProps> = ({
  data,
  disabled,
}) => {
  const { fields } = data;
  const statusField = useStatusField();
  const statusOptions = statusField?.metaData?.options || [];
  const [taskStatus, setTaskStatus] =
    useState<FieldValue<number> | undefined>();

  useDeepCompareEffect(() => {
    const result = data.fields.find(
      (f) => f.fieldId === statusField?.fieldId,
    ) as FieldValue<number>;

    setTaskStatus(result);
  }, [fields]);

  const selectedStatusOption = statusOptions.find(
    (status) => status.value === taskStatus?.fieldValue,
  );

  const dispatch = useDispatch();

  const handleStatusChange = (selected) => {
    const latest = {
      fieldId: statusField.fieldId,
      fieldValue: selected.value,
    };

    setTaskStatus(latest);
    dispatch(
      updateTaskSagaAction({
        projectId: data.project,
        taskId: data.taskId,
        data: {
          fields: [latest],
        },
      }),
    );
  };

  return (
    <StatusPicker
      type={data.type}
      dropdownHeader={<FormattedMessage id="task_status" />}
      field={statusField}
      onChange={handleStatusChange}
      value={taskStatus?.fieldValue as number}
      disabled={disabled}
    >
      <FormGroup>
        <StyledLabelForGhostInput>
          <FormattedMessage id="task_status" />
        </StyledLabelForGhostInput>
        <StatusPicker.Trigger
          progress={data.progress}
          selectedStatus={selectedStatusOption}
          showText
          disabled={disabled}
        />
      </FormGroup>
    </StatusPicker>
  );
};
